import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { UserRole } from '../types/rbac';

interface User {
  id: string;
  email?: string;
  name: string;
  role: UserRole;
}

interface LoginRequest {
  cpfOrEmail: string;
  password: string;
  role: UserRole;
}

interface RegisterRequest {
  name: string;
  email?: string;
  password: string;
  role: UserRole;
  cpf: string;
  city: string;
  state: string;
  phone?: string;
}

interface LoginResponse {
  user: User;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (data: LoginRequest) => Promise<LoginResponse>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => void;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // M7: usar o cliente api centralizado (com interceptores de auth e erro)
      api.get('/users/profile')
        .then((response: { data: User }) => {
          setUser(response.data);
        })
        .catch(() => {
          localStorage.removeItem('token');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post('/users/login', data);
    const { access_token, user } = response.data;

    localStorage.setItem('token', access_token);
    setUser(user);

    return { user };
  };

  const register = async (data: RegisterRequest): Promise<void> => {
    await api.post('/users/register', data);
  };

  const logout = (): void => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const forgotPassword = async (email: string): Promise<void> => {
    await api.post('/auth/forgot-password', { email });
  };

  const resetPassword = async (token: string, password: string): Promise<void> => {
    await api.post('/auth/reset-password', { token, newPassword: password });
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated: !!user,
      login,
      register,
      logout,
      forgotPassword,
      resetPassword
    }}>
      {children}
    </AuthContext.Provider>
  );
}; 